// src/utils/menu/productUtils.js

/**
 * Product-related utilities for menu
 */

/**
 * Deduplicate products by ID
 */
export const deduplicateProducts = (products) => {
    if (!products || products.length === 0) return [];
    
    const uniqueProductMap = new Map();
    
    products.forEach(product => {
        if (product && product.id && !uniqueProductMap.has(product.id)) {
            uniqueProductMap.set(product.id, product);
        }
    });
    
    return Array.from(uniqueProductMap.values());
};

/**
 * Calculate price range from products
 */
export const calculatePriceRange = (products, pricingType) => {
    if (!products || products.length === 0 || !pricingType) return null;
    
    const prices = products
        .flatMap(p => p.variants.map(v => 
            v?.[pricingType.special] ?? v?.[pricingType.standard]
        ))
        .filter(p => typeof p === 'number' && !isNaN(p));
    
    if (prices.length === 0) return null;
    
    const min = Math.floor(Math.min(...prices));
    const max = Math.ceil(Math.max(...prices));
    
    if (isNaN(min) || isNaN(max)) return null;
    
    return { min, max };
};

/**
 * Get products to display based on pagination factor
 */
export const getDisplayedProducts = (products, displayFactor, paginationLimit) => {
    const itemsToShow = displayFactor * paginationLimit;
    return products.slice(0, itemsToShow);
};

/**
 * Check if more products can be displayed
 */
export const canDisplayMoreProducts = (totalProducts, displayFactor, paginationLimit) => {
    return displayFactor * paginationLimit < totalProducts;
};

/**
 * Calculate required display factor for total products
 */
export const calculateRequiredDisplayFactor = (totalProducts, paginationLimit) => {
    return Math.ceil(totalProducts / paginationLimit);
};

/**
 * Sort products by price
 */
export const sortProductsByPrice = (products, pricingType, ascending = true) => {
    return [...products].sort((a, b) => {
        const priceA = getProductPrice(a, pricingType);
        const priceB = getProductPrice(b, pricingType);
        
        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        
        return ascending ? priceA - priceB : priceB - priceA;
    });
};

/**
 * Get product price based on pricing type
 */
export const getProductPrice = (product, pricingType) => {
    if (!product?.variants || product.variants.length === 0) return null;
    
    // Get the first variant's price
    const variant = product.variants[0];
    return variant?.[pricingType?.special] ?? variant?.[pricingType?.standard] ?? null;
};

/**
 * Filter products by price range
 */
export const filterProductsByPriceRange = (products, minPrice, maxPrice, pricingType) => {
    return products.filter(product => {
        const price = getProductPrice(product, pricingType);
        if (price === null) return false;
        
        if (minPrice !== undefined && price < minPrice) return false;
        if (maxPrice !== undefined && price > maxPrice) return false;
        
        return true;
    });
};

/**
 * Group products by category
 */
export const groupProductsByCategory = (products) => {
    const grouped = {};
    
    products.forEach(product => {
        const category = product.category || 'Other';
        if (!grouped[category]) {
            grouped[category] = [];
        }
        grouped[category].push(product);
    });
    
    return grouped;
};

/**
 * Check if product matches search query
 */
export const productMatchesSearch = (product, searchQuery) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const searchableFields = [
        product.name,
        product.brand,
        product.category,
        product.subcategory,
        ...(product.effects || []),
        ...(product.tags || [])
    ].filter(Boolean);
    
    return searchableFields.some(field => 
        field.toLowerCase().includes(query)
    );
};
